import React, { useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";
import PageHeading from "components/common/LayoutComponents/PageHeading";
import LoadingScreen from "components/common/LoadingScreen";
import SearchIcon from "@mui/icons-material/Search";
import Chip from "@mui/material/Chip";
import MarketplaceProductDialog from "./MarketplaceProductDialog";
import MarketplaceCategoryDialog from "./MarketplaceCategoryDialog";
import {
  StyledMarketplace,
  StyledHero,
  StyledHeroTitle,
  StyledHeroSubtitle,
  StyledFilters,
  StyledSearchField,
  StyledCategoryPills,
  StyledProductGrid,
  StyledProductCard,
  StyledProductHeader,
  StyledBadge,
  StyledProductDescription,
  StyledTags,
  StyledTag,
  StyledProductFooter,
  StyledAiNotice,
  StyledCategorySection,
  StyledCategoryHeader,
  StyledCategoryList,
  StyledCategoryCard,
  StyledCategoryActions,
} from "./Marketplace.style";
import {
  listMarketplaceCategories,
  listMarketplaceProducts,
  addMarketplaceProduct,
  editMarketplaceProduct,
  deleteMarketplaceProduct,
  addMarketplaceCategory,
  editMarketplaceCategory,
  deleteMarketplaceCategory,
} from "services/marketplace";

const hexToRgb = (hex = "#4c1d95") => {
  const sanitized = hex.replace("#", "");
  if (sanitized.length !== 6) return "76,29,149";
  const r = parseInt(sanitized.substring(0, 2), 16);
  const g = parseInt(sanitized.substring(2, 4), 16);
  const b = parseInt(sanitized.substring(4, 6), 16);
  return `${r},${g},${b}`;
};

const Marketplace = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryDialogLoading, setCategoryDialogLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const heroHighlights = useMemo(
    () => [
      "Merge curated vendors with NeuroGym AI coach recommendations.",
      "Explain how every product fits into focus, momentum, recovery, and celebration.",
      "Searchable, beautiful, affiliate-ready gateway for trusted partners.",
    ],
    []
  );

  const loadCategories = async () => {
    try {
      const response = await listMarketplaceCategories();
      setCategories(response || []);
    } catch (error) {
      console.error("Failed to load marketplace categories", error);
      enqueueSnackbar("Unable to load categories", { variant: "error" });
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await listMarketplaceProducts({
        search: searchTerm.trim() || undefined,
        categoryId: selectedCategoryId || undefined,
      });
      setProducts(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Failed to load marketplace products", error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [searchTerm, selectedCategoryId]);

  const handleAddProduct = () => {
    setSelectedProduct(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogSubmit = async (payload) => {
    setDialogLoading(true);
    try {
      if (selectedProduct) {
        await editMarketplaceProduct({ id: selectedProduct.id, ...payload });
        enqueueSnackbar("Product updated", { variant: "success" });
      } else {
        await addMarketplaceProduct(payload);
        enqueueSnackbar("Product created", { variant: "success" });
      }
      await loadProducts();
      handleDialogClose();
    } catch (error) {
      console.error("Failed to save marketplace product", error);
      enqueueSnackbar(error?.message || "Unable to save product", { variant: "error" });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async (product) => {
    setLoading(true);
    try {
      await deleteMarketplaceProduct(product.id);
      enqueueSnackbar("Product removed", { variant: "success" });
      await loadProducts();
    } catch (error) {
      console.error("Failed to delete product", error);
      enqueueSnackbar("Unable to delete product", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleTabCategoryClick = (categoryId) => {
    setSelectedCategoryId((prev) => (prev === categoryId ? null : categoryId));
  };

  const handleAddCategory = () => {
    setSelectedCategory(null);
    setCategoryDialogOpen(true);
  };

  const handleCategoryDialogClose = () => {
    setCategoryDialogOpen(false);
    setSelectedCategory(null);
  };

  const handleCategorySubmit = async (payload) => {
    setCategoryDialogLoading(true);
    try {
      if (selectedCategory) {
        await editMarketplaceCategory(payload);
        enqueueSnackbar("Category updated", { variant: "success" });
      } else {
        await addMarketplaceCategory(payload);
        enqueueSnackbar("Category created", { variant: "success" });
      }
      await loadCategories();
      handleCategoryDialogClose();
    } catch (error) {
      console.error("Failed to save marketplace category", error);
      const msg = error?.message || "Unable to save category (check slug is unique)";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setCategoryDialogLoading(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    const ok = window.confirm(
      `Delete category "${category.name}"? Any products assigned to it will be removed from the marketplace.`
    );
    if (!ok) return;
    setLoading(true);
    try {
      await deleteMarketplaceCategory(category.id);
      enqueueSnackbar("Category removed", { variant: "success" });
      if (selectedCategoryId === category.id) {
        setSelectedCategoryId(null);
      }
      await loadCategories();
      await loadProducts();
    } catch (error) {
      console.error("Failed to delete marketplace category", error);
      enqueueSnackbar(error?.message || "Unable to delete category", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledMarketplace>
      <PageHeading>
        <div className="heading-copy">
          <h1 className="page-title">Marketplace</h1>
          <p className="page-subtitle">
            Curate approved vendors, articulate how each product supports the NeuroGym journey, and keep AI coaches aligned
            with the experience.
          </p>
        </div>
        <div className="actions">
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddProduct}
            disabled={categories.length === 0}
            title={categories.length === 0 ? "Create at least one category before adding products" : ""}
          >
            Add Product
          </Button>
        </div>
      </PageHeading>
      <StyledHero>
        <StyledHeroTitle>World-class marketplace for neuro-fitness guidance</StyledHeroTitle>
        <StyledHeroSubtitle>
          Showcase trusted tools and products with explanations, AI coach context, and direct product journeys that invite
          exploration.
        </StyledHeroSubtitle>
        <Box component="ul" sx={{ margin: "16px 0 0", paddingLeft: "20px", color: "#fdf8ff" }}>
          {heroHighlights.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </Box>
      </StyledHero>
      <StyledFilters>
        <StyledSearchField>
          <TextField
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search vendors, focus areas, or tags"
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </StyledSearchField>
        <StyledCategoryPills>
          <Chip
            label="All categories"
            variant={!selectedCategoryId ? "filled" : "outlined"}
            color={!selectedCategoryId ? "primary" : "default"}
            onClick={() => handleTabCategoryClick(null)}
          />
          {categories.map((category) => (
            <Chip
              key={`category-chip-${category.id}`}
              label={category.name}
              variant={selectedCategoryId === category.id ? "filled" : "outlined"}
              color={selectedCategoryId === category.id ? "primary" : "default"}
              onClick={() => handleTabCategoryClick(category.id)}
            />
          ))}
        </StyledCategoryPills>
      </StyledFilters>

      <StyledCategorySection>
        <StyledCategoryHeader>
          <div>
            <Typography variant="h6" component="h2" gutterBottom>
              Marketplace categories
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Create and edit categories used for filters and product assignment. Slugs must stay unique.
            </Typography>
          </div>
          <Button variant="contained" color="primary" size="small" onClick={handleAddCategory}>
            Add Category
          </Button>
        </StyledCategoryHeader>
        <StyledCategoryList>
          {categories.map((cat) => (
            <StyledCategoryCard key={`category-card-${cat.id}`}>
              <Typography variant="subtitle1" fontWeight={600}>
                {cat.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                /{cat.slug}
              </Typography>
              {cat.description && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  {cat.description}
                </Typography>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 1 }}>
                {cat.featured && <Chip label="Featured" size="small" color="secondary" />}
                <Typography variant="caption" color="textSecondary">
                  Sort: {cat.sortOrder ?? 0}
                </Typography>
                {cat.accentColor && (
                  <Box
                    component="span"
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      border: (theme) =>
                        `1px solid ${theme.palette.divider}`,
                      backgroundColor: cat.accentColor,
                    }}
                    title={cat.accentColor}
                  />
                )}
              </Box>
              <Box sx={{ marginTop: "auto", paddingTop: 1 }}>
                <StyledCategoryActions>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      setSelectedCategory(cat);
                      setCategoryDialogOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button size="small" variant="text" color="error" onClick={() => handleDeleteCategory(cat)}>
                    Delete
                  </Button>
                </StyledCategoryActions>
              </Box>
            </StyledCategoryCard>
          ))}
        </StyledCategoryList>
        {categories.length === 0 && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            No categories yet. Add one to start assigning products.
          </Typography>
        )}
      </StyledCategorySection>

      <StyledProductGrid>
        {products.map((product) => (
          <StyledProductCard key={`marketplace-card-${product.id}`}>
            <StyledProductHeader>
              <Box>
                <Typography variant="h6">{product.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {product.tagline}
                </Typography>
              </Box>
              <StyledBadge tint={product.categoryAccentColor ? hexToRgb(product.categoryAccentColor) : "76,29,149"}>
                {product.categoryName || "Marketplace"}
              </StyledBadge>
            </StyledProductHeader>
            <StyledProductDescription>{product.description}</StyledProductDescription>
            <StyledTags>
              {(product.tags || "").split(",").map((tag) => {
                const trimmed = tag.trim();
                if (!trimmed) return null;
                return <StyledTag key={`${product.id}-tag-${trimmed}`}>{trimmed}</StyledTag>;
              })}
            </StyledTags>
            {product.aiCoachNotes && <StyledAiNotice>{product.aiCoachNotes}</StyledAiNotice>}
            <StyledProductFooter>
              <Typography variant="caption" color="textSecondary">
                {product.journeyStage ? `${product.journeyStage} • ` : ""}
                {product.priceRange || "Priced per vendor"}
              </Typography>
              <Box sx={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setSelectedProduct(product);
                    setDialogOpen(true);
                  }}
                >
                  Edit
                </Button>
                <Button variant="text" size="small" color="error" onClick={() => handleDelete(product)}>
                  Delete
                </Button>
                {product.ctaUrl && (
                  <Button variant="contained" size="small" href={product.ctaUrl} target="_blank">
                    {product.ctaLabel || "Explore"}
                  </Button>
                )}
              </Box>
              {product.coachHighlight && (
                <Typography variant="body2" color="textSecondary">
                  {product.coachHighlight}
                </Typography>
              )}
            </StyledProductFooter>
          </StyledProductCard>
        ))}
      </StyledProductGrid>
      <MarketplaceProductDialog
        open={dialogOpen}
        categories={categories}
        product={selectedProduct}
        loading={dialogLoading}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
      />
      <MarketplaceCategoryDialog
        open={categoryDialogOpen}
        category={selectedCategory}
        loading={categoryDialogLoading}
        onClose={handleCategoryDialogClose}
        onSubmit={handleCategorySubmit}
      />
      <LoadingScreen loading={loading || dialogLoading || categoryDialogLoading} />
    </StyledMarketplace>
  );
};

export default Marketplace;
